import React from 'react';
import { CommentsPopper, DateRow, LinkRow, ManageActions, StatusCard, TextRow } from 'components';
import { commentModels, Images, KeyPurpose, PermissionList, RolePermission } from 'utils';

/**
 * Mirrors `WishlistCategory` / `WishlistStatus` in @therapylake/package/wishlist.
 * Hand-copied because the frontend build cannot import package enums — see the
 * note at the top of utils/constants.js.
 */
export const wishlistCategoryEnums = {
    TASK: 'TASK',
    FEATURE: 'FEATURE',
    BUG: 'BUG',
};

export const wishlistStatusEnums = {
    NOT_STARTED: 'NOT_STARTED',
    PLANNED: 'PLANNED',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    DECLINED: 'DECLINED',
};

export const wishlistCategoryList = [
    { id: wishlistCategoryEnums.FEATURE, name: 'Feature', hint: 'Software enhancements, tools, etc.' },
    { id: wishlistCategoryEnums.BUG, name: 'Bug', hint: 'Something that does not work correctly' },
    { id: wishlistCategoryEnums.TASK, name: 'Task', hint: 'Work TherapyLake should do for your org' },
];

export const wishlistStatusList = [
    { id: wishlistStatusEnums.NOT_STARTED, name: 'Not Started', hint: 'Work on this request has not started yet.' },
    {
        id: wishlistStatusEnums.PLANNED,
        name: 'Planned',
        hint: 'This request has been reviewed and is planned for future implementation.',
    },
    { id: wishlistStatusEnums.IN_PROGRESS, name: 'In Progress', hint: 'Work on this request is currently in progress.' },
    { id: wishlistStatusEnums.COMPLETED, name: 'Completed', hint: 'Feature/request has been implemented.' },
    { id: wishlistStatusEnums.DECLINED, name: 'Declined', hint: 'This request was reviewed and will not be implemented.' },
];

// `{ backgroundColor, circleColor, color }` — the `custom` shape <StatusCard> takes.
const categoryColors = {
    [wishlistCategoryEnums.FEATURE]: { backgroundColor: '#EFF8FF', circleColor: '#2E90FA', color: '#175CD3' },
    [wishlistCategoryEnums.BUG]: { backgroundColor: '#FEF3F2', circleColor: '#F04438', color: '#B42318' },
    [wishlistCategoryEnums.TASK]: { backgroundColor: '#F2F4F7', circleColor: '#667085', color: '#344054' },
};

const statusColors = {
    [wishlistStatusEnums.NOT_STARTED]: { backgroundColor: '#F2F4F7', circleColor: '#667085', color: '#344054' },
    [wishlistStatusEnums.PLANNED]: { backgroundColor: '#F4F3FF', circleColor: '#7A5AF8', color: '#5925DC' },
    [wishlistStatusEnums.IN_PROGRESS]: { backgroundColor: '#EFF8FF', circleColor: '#2E90FA', color: '#175CD3' },
    [wishlistStatusEnums.COMPLETED]: { backgroundColor: '#ECFDF3', circleColor: '#12B76A', color: '#067647' },
    [wishlistStatusEnums.DECLINED]: { backgroundColor: '#FEF3F2', circleColor: '#F04438', color: '#B42318' },
};

const nameOf = (list, id) => list.find((i) => i.id === id)?.name || id || '--';

export const CategoryChip = ({ category }) =>
    category ? <StatusCard status={nameOf(wishlistCategoryList, category)} custom={categoryColors[category]} noCircle={true} /> : '--';

export const StatusChip = ({ status }) =>
    status ? <StatusCard status={nameOf(wishlistStatusList, status)} custom={statusColors[status]} /> : '--';

/** Upload purpose for request and comment attachments — the one admin_api is allowed to read. */
export const WISHLIST_ATTACHMENT_PURPOSE = KeyPurpose.WISHLIST_ATTACHMENT;

/** Accepted by the uploader; mirrors CONTENT_TYPE_ALLOWLIST_BY_PURPOSE[WISHLIST_ATTACHMENT]. */
export const WISHLIST_ATTACHMENT_TYPES = ['PNG', 'JPG', 'JPEG', 'WEBP', 'PDF', 'CSV'];

export const isOwnRequest = (item, userId) => !!userId && item?.createdBy?.userId === userId;

/**
 * Column headers, in the grid's own conventions: Category and Status filter
 * from their header dropdowns (like Tasks' Status), and the three text columns
 * search server-side through `searchKey` (like Staff). The api searches
 * request ID, title, description and requester together, so all three keys
 * send the same `search` — see renderParams in wishlist.js. No server-side
 * sort yet, hence `custom: false` on the text columns.
 */
export const wishlistHead = [
    { name: 'requestNumber', title: 'Request ID', searchKey: 'REQUEST_ID', custom: false, width: '170px' },
    { name: 'title', title: 'Request', searchKey: 'TITLE', custom: false },
    { name: 'description', title: 'Description', custom: false },
    {
        name: 'category',
        title: 'Category',
        filterList: [{ name: 'All', id: 'All' }, ...wishlistCategoryList],
        width: '140px',
    },
    { name: 'createdBy', title: 'Requested By', searchKey: 'REQUESTED_BY', custom: false },
    {
        name: 'status',
        title: 'Status',
        filterList: [{ name: 'All', id: 'All' }, ...wishlistStatusList],
        width: '150px',
    },
    { name: 'proposedCompletionDate', title: 'Proposed Completion', custom: false, disabled: true, width: '175px' },
    { name: '', title: 'Action', custom: false, disabled: true, width: '70px' },
];

/**
 * `onSeen(item)` clears a row's red "updated" dot. Opening the comments counts
 * as opening the request: a TherapyLake reply is usually what lit the dot.
 */
export const wishlistBody = (userId, afterCommentsChange, onSeen) => [
    {
        rowText: (item) => (
            <div className="text-style flex-align-center" style={{ gap: 8 }}>
                {item?.hasUpdate && (
                    <span className="wishlist-updated-dot" title="Updated by TherapyLake" aria-label="Updated by TherapyLake" />
                )}
                {item?.requestNumber}
                <CommentsPopper
                    onOpen={() => item?.hasUpdate && onSeen?.(item)}
                    buttonClassName="comments-trigger-inline"
                    count={item?.commentCount}
                    resource={commentModels.Wishlist}
                    resourceId={item?.id}
                    title={`Request ${item?.requestNumber}`}
                    attachmentPurpose={WISHLIST_ATTACHMENT_PURPOSE}
                    enableMentions={false}
                    afterSuccess={afterCommentsChange}
                />
            </div>
        ),
        notClickable: true,
    },
    { rowText: (item) => <TextRow name={item?.title} /> },
    { rowText: (item) => <TextRow name={item?.description} /> },
    { rowText: (item) => <CategoryChip category={item?.category} /> },
    {
        rowText: (item) => (
            <LinkRow
                name={item?.createdBy?.name || '--'}
                href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.createdBy?.userId}` : ''}
                checkPermission={item?.createdBy?.userId ? RolePermission([PermissionList.STAFF_READ.code]) : null}
            />
        ),
    },
    { rowText: (item) => <StatusChip status={item?.status} /> },
    { rowText: (item) => <DateRow date={item?.proposedCompletionDate} /> },
    {
        button: (fn, item) => (
            <ManageActions
                btnClass="edit-btn-box"
                btnIcon={Images.moreGrayIcon}
                styleBodyItems={{ padding: 0 }}
                renderBody={(handleClose) => (
                    <div className="popover-action-btn">
                        <button
                            onClick={() => {
                                handleClose();
                                fn('view');
                            }}
                        >
                            <img src={Images.showEye} alt="icon" />
                            <p>View Details</p>
                        </button>
                        {/* Only the person who filed a request may change it; the api
                            enforces the same rule, this just hides a button that would 403. */}
                        {isOwnRequest(item, userId) && (
                            <>
                                <button
                                    onClick={() => {
                                        handleClose();
                                        fn('edit');
                                    }}
                                >
                                    <img src={Images.edit} alt="icon" />
                                    <p>Edit</p>
                                </button>
                                <button
                                    onClick={() => {
                                        handleClose();
                                        fn('delete');
                                    }}
                                >
                                    <img src={Images.deleteTag} alt="icon" />
                                    <p>Delete</p>
                                </button>
                            </>
                        )}
                    </div>
                )}
            />
        ),
        notClickable: true,
    },
];

export const WISHLIST_LIST_ACTION = 'GET_WISHLIST_ITEMS';
export const WISHLIST_PAGE_SIZE = 50;

export const wishlistNotYet = {
    title: 'No requests yet',
    subTitle: "Your organization's wishlist is empty. Submit a request to share a feature, task, or bug with the TherapyLake team.",
    image: Images.noClientYet,
};

export const wishlistNoMatches = {
    title: 'No matching requests',
    subTitle: 'No requests match these filters. Try a different category, status, or search.',
    image: Images.noClientYet,
};
